import type React from "react"
import { useTranslation } from "react-i18next"
import { useCallback, useMemo, useState } from "react"
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import {
  InfoOutlined as InfoOutlinedIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from "@mui/icons-material"
import api from "../../services/api"
import { logoParaDataUri } from "../../utils/partnerLogo"
import { supportedLanguages } from "../../i18n"
import { getScope } from "../../hooks/useScope"
import EntityList from "../../components/EntityList"
import { Partner } from "../../interfaces"
import { useCryptoNavigation } from "../../App"

/**
 * Lista de parceiros — a camada acima do proprietário.
 *
 * O parceiro 0 é a raiz. Quem decide se esta tela aparece é o menu
 * (layouts/Dashboard) e, dentro dela, o EntityList com entityType="partners",
 * que consulta o resolvedor de escopo para liberar criar/editar/excluir.
 */
export default function PartnerList() {
  const { t } = useTranslation()
  const { navigateToPage } = useCryptoNavigation()
  const escopo = getScope()
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [alternando, setAlternando] = useState(false)
  const [erroStatus, setErroStatus] = useState<string | null>(null)
  const [selecionado, setSelecionado] = useState<Partner | null>(null)
  const [detalhesAbertos, setDetalhesAbertos] = useState(false)
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"))

  /** Abre os detalhes — é o que o botão de informações da lista aciona. */
  const handleShowDetails = useCallback((partner: Partner) => {
    setSelecionado(partner)
    setDetalhesAbertos(true)
  }, [])

  const handleNew = useCallback(() => {
    navigateToPage("partners", { mode: "new" })
  }, [navigateToPage])

  const handleEdit = useCallback(
    (partnerId: number | string) => {
      navigateToPage("partners", { mode: "edit", id: String(partnerId) })
    },
    [navigateToPage],
  )

  const columns = useMemo(
    () => [
      {
        id: "partnerId",
        label: t("fields.id"),
        minWidth: 70,
        width: "8%",
        align: "center" as const,
        mobileHideLabel: true,
      },
      {
        id: "partnerName",
        label: t("fields.name"),
        minWidth: 150,
      },
      {
        id: "partnerNickName",
        label: t("fields.nickname"),
        minWidth: 120,
        format: (value: string) => value || "-",
      },
      {
        id: "partnerStatus",
        label: t("fields.status"),
        minWidth: 90,
        width: "10%",
        align: "center" as const,
        mobileHideLabel: true,
        // Ausente (backend antigo) é tratado como ativo — um parceiro sem o
        // campo não deve aparecer como desligado.
        format: (value: number | undefined) => (
          <Chip
            label={value === 0 ? t("common.inactive") : t("common.active")}
            color={value === 0 ? "error" : "success"}
            size="small"
            variant="outlined"
          />
        ),
      },
    ],
    [t],
  )

  /**
   * Normaliza o id. O backend varia o nome do campo entre rotas, e cair no
   * zero silenciosamente faria o botão de editar pedir /partner/0 — que não
   * existe, e o erro apareceria como "falha ao carregar" sem pista nenhuma.
   */
  const transformData = useCallback((data: any[]): Partner[] => {
    return data.map((partner) => {
      const bruto =
        partner.partnerId ?? partner.partnerID ?? partner.id ?? partner.partner_id
      const idNumerico =
        bruto === null || bruto === undefined || bruto === ""
          ? null
          : Number(bruto)

      if (idNumerico === null || Number.isNaN(idNumerico)) {
        console.warn("Parceiro sem id reconhecível:", partner)
      }

      return {
        ...partner,
        partnerId: idNumerico ?? undefined,
      }
    })
  }, [])

  /**
   * Liga/desliga o parceiro SEM passar pelo formulário.
   *
   * Trocar o interruptor não deveria exigir cadastro completo: há parceiros
   * gravados sem todos os campos, e o formulário — que valida tudo como
   * obrigatório — simplesmente não deixava salvar, bloqueando uma operação
   * que não tem relação nenhuma com os campos faltantes.
   *
   * Relê o registro inteiro e reescreve trocando só a situação, como o salvar
   * do ROI da câmera faz: um PUT parcial poderia zerar as outras colunas.
   */
  const alternarStatus = useCallback(
    async (partner: Partner) => {
      const pid = partner.partnerId
      if (pid === undefined || pid === null) return
      setAlternando(true)
      setErroStatus(null)
      try {
        const resp = await api.get(`/partner/${pid}`)
        const corpo = resp.data
        const atual =
          corpo?.partner ??
          (Array.isArray(corpo?.partners) ? corpo.partners[0] : corpo)

        const novoStatus = (atual?.partnerStatus ?? 1) === 0 ? 1 : 0
        await api.put(`/partner/${pid}`, { ...atual, partnerStatus: novoStatus })

        // CONFERE antes de afirmar. Um 2xx diz que a requisição foi aceita,
        // não que o campo foi gravado: se a rota de atualização ainda não
        // conhece `partnerStatus`, ela responde 200 e ignora o campo. Sem esta
        // releitura a tela dizia "ativado" com o registro intacto no banco.
        const confere = await api.get(`/partner/${pid}`)
        const corpoDepois = confere.data
        const depois =
          corpoDepois?.partner ??
          (Array.isArray(corpoDepois?.partners) ? corpoDepois.partners[0] : corpoDepois)
        const gravado = depois?.partnerStatus

        if (Number(gravado) !== novoStatus) {
          setErroStatus(
            t("partners.statusNotPersisted", {
              enviado: novoStatus,
              gravado: gravado === undefined ? "ausente" : String(gravado),
            }),
          )
          return
        }

        setSelecionado((s) => (s ? { ...s, partnerStatus: novoStatus } : s))
        setRefreshTrigger((v) => v + 1)
      } catch (e: any) {
        const status = e?.response?.status
        console.error("Erro ao alternar situação do parceiro:", e)
        setErroStatus(
          status ? `${t("partners.saveError")} (HTTP ${status})` : t("partners.saveError"),
        )
      } finally {
        setAlternando(false)
      }
    },
    [t],
  )

  // Sem permissão: interrompe DIZENDO o motivo, em vez de sumir. São duas
  // causas distintas, e a diferença importa para diagnosticar.
  if (!escopo.canManagePartners) {
    const desconhecido = !escopo.partnerKnown
    return (
      <Box sx={{ p: 3, maxWidth: 760, mx: "auto" }}>
        <Alert severity="warning" icon={<InfoOutlinedIcon />}>
          <AlertTitle>
            {desconhecido ? t("partners.unknownTitle") : t("partners.deniedTitle")}
          </AlertTitle>
          {desconhecido ? t("partners.unknownBody") : t("partners.deniedBody")}
          {/* Valores lidos, para tornar o bloqueio diagnosticável. */}
          <Box
            component="pre"
            sx={{
              mt: 1.5,
              mb: 0,
              fontSize: 12,
              opacity: 0.8,
              whiteSpace: "pre-wrap",
            }}
          >
            {`partner: ${desconhecido ? "—" : String(escopo.partnerId)}`}
            {`  ·  owner: ${escopo.tenantId === null ? "—" : String(escopo.tenantId)}`}
            {`  ·  role: ${escopo.userRole === null ? "—" : String(escopo.userRole)}`}
          </Box>
        </Alert>
      </Box>
    )
  }

  const nomeDoIdioma = (code?: string | null) =>
    supportedLanguages.find((l) => l.code === code)?.name ?? "—"

  const linha = (rotulo: string, valor?: string | number | null) => (
    <Typography sx={{ fontSize: { xs: "0.85rem", sm: "1rem" } }}>
      <strong>{rotulo}:</strong>{" "}
      {valor === null || valor === undefined || valor === "" ? "—" : valor}
    </Typography>
  )

  return (
    <>
    <EntityList
      title={t("nav.partners")}
      endpoint="/partner"
      columns={columns}
      addPath=""
      editPathPrefix=""
      idField="partnerId"
      dataPath="partners"
      entityType="partners"
      onRowClick={handleShowDetails}
      refreshTrigger={refreshTrigger}
      transformData={transformData}
      useEncryptedNavigation={true}
      encryptedPageName="partners"
      onAddClick={handleNew}
      onEditClick={handleEdit}
    />

    {/* Detalhes do parceiro — acionado pelo botão de informações da lista. */}
    <Dialog
      open={detalhesAbertos}
      onClose={() => setDetalhesAbertos(false)}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
    >
      {selecionado && (
        <>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              p: { xs: 2, sm: 3 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              {logoParaDataUri(selecionado.partnerLogo) && (
                <Box
                  component="img"
                  src={logoParaDataUri(selecionado.partnerLogo) as string}
                  alt={selecionado.partnerName}
                  sx={{ height: 28, maxWidth: 140, objectFit: "contain" }}
                />
              )}
              <Typography
                variant="h6"
                noWrap
                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                {selecionado.partnerName}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                color={selecionado.partnerStatus === 0 ? "error" : "success"}
                label={
                  selecionado.partnerStatus === 0
                    ? t("common.inactive")
                    : t("common.active")
                }
              />
            </Box>
            <IconButton edge="end" onClick={() => setDetalhesAbertos(false)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("nav.partners")}
                </Typography>
                <Box sx={{ mt: 1, mb: 2 }}>
                  {linha(t("fields.id"), selecionado.partnerId)}
                  {linha(t("fields.name"), selecionado.partnerName)}
                  {linha(t("fields.nickname"), selecionado.partnerNickName)}
                  {linha(t("fields.fiscalCode"), selecionado.partnerFiscalCode)}
                  {linha(t("fields.phone"), selecionado.partnerPhone)}
                  {linha(t("common.language"), nomeDoIdioma(selecionado.partnerLanguage))}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("fields.address")}
                </Typography>
                <Box sx={{ mt: 1, mb: 2 }}>
                  {linha(t("fields.address"), selecionado.partnerAddress)}
                  {linha(t("fields.city"), selecionado.partnerCity)}
                  {linha(t("fields.state"), selecionado.partnerState)}
                  {linha(t("fields.country"), selecionado.partnerCountry)}
                  {linha(t("fields.zipcode"), selecionado.partnerZipcode)}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ gap: 1 }}>
            {erroStatus && (
              <Typography variant="caption" color="error" sx={{ mr: "auto" }}>
                {erroStatus}
              </Typography>
            )}
            <Button onClick={() => setDetalhesAbertos(false)}>
              {t("common.close")}
            </Button>
            {escopo.canTogglePartnerStatus && (
              <Button
                variant="outlined"
                color={selecionado.partnerStatus === 0 ? "success" : "warning"}
                disabled={alternando}
                startIcon={
                  selecionado.partnerStatus === 0 ? <ToggleOnIcon /> : <ToggleOffIcon />
                }
                onClick={() => void alternarStatus(selecionado)}
              >
                {/* AÇÃO, não estado. A etiqueta do topo já diz a situação
                    atual ("Ativo"); rotular o botão com outra palavra de
                    estado ("Inativo") fazia a tela parecer afirmar as duas
                    coisas ao mesmo tempo. */}
                {selecionado.partnerStatus === 0
                  ? t("common.activate")
                  : t("common.deactivate")}
              </Button>
            )}
            {escopo.canManagePartners && selecionado.partnerId !== undefined && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  setDetalhesAbertos(false)
                  handleEdit(selecionado.partnerId as number)
                }}
              >
                {t("partners.editTitle")}
              </Button>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
    </>
  )
}
